import { endOfMonth, startOfDay, startOfMonth } from "@clipboard-health/date-time";
import { type CalendarNavigator, type DateRange } from "@src/appV2/lib/Calendar/types";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { addMonths, isBefore, isWithinInterval, subMonths } from "date-fns";
import { useState } from "react";

export function useMonthRange(): CalendarNavigator {
  const worker = useDefinedWorker();
  const timeZone = worker.tmz ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
  const currentDate = startOfDay(new Date(), { timeZone });
  const [dateRange, setDateRange] = useState<DateRange>({
    startDate: currentDate,
    endDate: endOfMonth(currentDate, { timeZone }),
  });

  function onPrevious() {
    const newFromDate = startOfMonth(subMonths(dateRange.startDate, 1), { timeZone });
    const newToDate = endOfMonth(newFromDate, { timeZone });

    setDateRange({
      startDate: isBefore(newFromDate, currentDate) ? currentDate : newFromDate,
      endDate: newToDate,
    });
  }

  function onNext() {
    const newFromDate = startOfMonth(addMonths(dateRange.startDate, 1), {
      timeZone,
    });
    const newToDate = endOfMonth(newFromDate, { timeZone });

    setDateRange({
      startDate: newFromDate,
      endDate: newToDate,
    });
  }

  const isFirstMonth = isWithinInterval(currentDate, {
    start: dateRange.startDate,
    end: dateRange.endDate,
  });

  return {
    isFirstMonth,
    dateRange,
    onPrevious,
    onNext,
  };
}
